<template>
  <div>
    <div class="container-fluid bg-light py-6 mt-0">
      <div class="container text-center animated bounceInDown">
        <h1 class="display-1 mb-4">{{ $t("galleryBook") }}</h1>
        <ol class="breadcrumb justify-content-center mb-0 animated bounceInDown">
          <li class="breadcrumb-item">
            <router-link to="/">{{ $t("home") }}</router-link>
          </li>
          <li class="breadcrumb-item text-dark" aria-current="page">
            {{ $t("galleryBook") }}
          </li>
        </ol>
      </div>
    </div>

    <div class="container-fluid py-6">
      <div class="container">
        <div v-if="isLoading" class="row g-4">
          <div v-for="n in 6" :key="n" class="col-md-6 col-lg-4">
            <div class="media-card">
              <div class="media-card__thumb skeleton"></div>
              <div class="p-3">
                <div class="skeleton skeleton-line w-75"></div>
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="hasError" class="text-center py-5">
          <i class="fas fa-exclamation-triangle fa-3x text-primary mb-3"></i>
          <p class="mb-4">{{ $t("somethingWentWrong") }}</p>
          <button class="btn btn-primary px-4 py-2 rounded-pill" @click="loadMedia">
            {{ $t("tryAgain") }}
          </button>
        </div>

        <div v-else-if="isEmpty" class="text-center py-5">
          <i class="fas fa-photo-video fa-3x text-primary mb-3"></i>
          <p class="mb-0">{{ $t("noMediaYet") }}</p>
        </div>

        <template v-else>
          <section
            v-for="(items, typeName) in groupedVideos"
            :key="`v-${typeName}`"
            class="mb-6"
          >
            <div class="d-flex align-items-center mb-4">
              <h2 class="h3 mb-0 me-3">{{ typeName }}</h2>
              <span class="badge rounded-pill media-count">{{ items.length }}</span>
            </div>

            <div class="row g-4">
              <div
                v-for="video in items"
                :key="video.id"
                class="col-md-6 col-lg-4 wow bounceInUp"
                data-wow-delay="0.1s"
              >
                <button type="button" class="media-card w-100 text-start" @click="openVideo(video)">
                  <div class="media-card__thumb">
                    <img
                      v-if="isYouTube(video.url)"
                      :src="youTubeThumb(video.url)"
                      :alt="video.title"
                      class="media-card__img"
                      loading="lazy"
                      @error="useLogoFallback"
                    />
                    <!-- preload="metadata" pulls only the header, not the whole file -->
                    <video
                      v-else
                      :src="video.url"
                      class="media-card__img"
                      preload="metadata"
                      muted
                      playsinline
                    ></video>
                    <span class="media-card__play" aria-hidden="true">
                      <i class="fas fa-play"></i>
                    </span>
                  </div>
                  <div class="p-3">
                    <h6 class="mb-1 text-truncate">{{ video.title }}</h6>
                    <small v-if="video.size" class="text-muted">
                      {{ formatSize(video.size) }}
                    </small>
                  </div>
                </button>
              </div>
            </div>
          </section>

          <section v-for="gallery in photoGalleries" :key="`p-${gallery.id}`" class="mb-6">
            <h2 class="h3 mb-4">{{ gallery.title }}</h2>
            <div class="row g-4">
              <div
                v-for="file in gallery.files"
                :key="file.id"
                class="col-6 col-md-4 col-lg-3 wow bounceInUp"
                data-wow-delay="0.1s"
              >
                <button type="button" class="media-card w-100" @click="openImage(gallery, file)">
                  <div class="media-card__thumb media-card__thumb--square">
                    <img
                      :src="mediaUrl(file.url)"
                      :alt="gallery.title"
                      class="media-card__img"
                      loading="lazy"
                      @error="useLogoFallback"
                    />
                  </div>
                </button>
              </div>
            </div>
          </section>
        </template>
      </div>
    </div>

    <div
      v-if="activeItem"
      class="media-modal"
      role="dialog"
      aria-modal="true"
      @click.self="closeModal"
    >
      <div class="media-modal__inner">
        <button type="button" class="media-modal__close" :aria-label="$t('close')" @click="closeModal">
          <i class="fas fa-times"></i>
        </button>

        <div class="media-modal__body">
          <iframe
            v-if="activeType === 'video' && isYouTube(activeItem.url)"
            :src="youTubeEmbed(activeItem.url)"
            class="media-modal__frame"
            title="Video player"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          ></iframe>

          <video
            v-else-if="activeType === 'video'"
            :src="activeItem.url"
            class="media-modal__frame"
            controls
            autoplay
            playsinline
            controlsList="nodownload"
          ></video>

          <img v-else :src="mediaUrl(activeItem.url)" class="media-modal__frame" :alt="activeTitle" />
        </div>

        <p class="media-modal__caption mb-0">{{ activeTitle }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { mediaUrl } from "@/utils/mediaUrl";

const FALLBACK_IMAGE = "/img/logo.jpg";
const YOUTUBE_HOSTS = ["youtube.com", "youtu.be"];

export default {
  name: "GalleryBook",
  data() {
    return {
      isLoading: true,
      hasError: false,
      activeItem: null,
      activeType: null,
      activeTitle: "",
    };
  },
  computed: {
    ...mapGetters(["galleries", "videos"]),
    photoGalleries() {
      return (this.galleries || []).filter((g) => g.files && g.files.length);
    },
    groupedVideos() {
      return (this.videos || []).reduce((groups, video) => {
        const name = (video.gallery_type && video.gallery_type.name) || "Videos";
        (groups[name] = groups[name] || []).push(video);
        return groups;
      }, {});
    },
    isEmpty() {
      return !this.videos.length && !this.photoGalleries.length;
    },
  },
  methods: {
    ...mapActions(["getGalleries", "getVideos"]),
    mediaUrl,
    async loadMedia() {
      this.isLoading = true;
      this.hasError = false;

      try {
        await Promise.all([this.getGalleries(), this.getVideos()]);
      } catch (error) {
        console.error("Failed to load gallery media:", error);
        this.hasError = true;
      } finally {
        this.isLoading = false;
      }
    },
    isYouTube(url) {
      return !!url && YOUTUBE_HOSTS.some((host) => url.includes(host));
    },
    youTubeId(url) {
      if (!url) return null;

      const patterns = [
        /[?&]v=([\w-]{11})/,
        /youtu\.be\/([\w-]{11})/,
        /\/embed\/([\w-]{11})/,
        /\/shorts\/([\w-]{11})/,
      ];

      for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match) return match[1];
      }

      return null;
    },
    youTubeEmbed(url) {
      const id = this.youTubeId(url);
      return id ? `https://www.youtube.com/embed/${id}?autoplay=1` : url;
    },
    youTubeThumb(url) {
      const id = this.youTubeId(url);
      return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : FALLBACK_IMAGE;
    },
    useLogoFallback(event) {
      if (!event.target.src.endsWith(FALLBACK_IMAGE)) {
        event.target.src = FALLBACK_IMAGE;
      }
    },
    formatSize(bytes) {
      if (!bytes) return "";
      const mb = bytes / (1024 * 1024);
      return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb.toFixed(1)} MB`;
    },
    openVideo(video) {
      this.activeItem = video;
      this.activeType = "video";
      this.activeTitle = video.title;
      this.lockScroll(true);
    },
    openImage(gallery, file) {
      this.activeItem = file;
      this.activeType = "image";
      this.activeTitle = gallery.title;
      this.lockScroll(true);
    },
    closeModal() {
      this.activeItem = null;
      this.activeType = null;
      this.activeTitle = "";
      this.lockScroll(false);
    },
    lockScroll(locked) {
      document.body.style.overflow = locked ? "hidden" : "";
    },
    onKeydown(event) {
      if (event.key === "Escape" && this.activeItem) {
        this.closeModal();
      }
    },
  },
  mounted() {
    this.loadMedia();
    document.addEventListener("keydown", this.onKeydown);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onKeydown);
    this.lockScroll(false);
  },
};
</script>

<style scoped>
.mb-6 {
  margin-bottom: 4rem;
}

.media-count {
  background: #5c1a72;
  color: #fff;
}

.media-card {
  display: block;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  overflow: hidden;
  padding: 0;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.media-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.12);
}

.media-card:focus-visible {
  outline: 3px solid #5c1a72;
  outline-offset: 2px;
}

.media-card__thumb {
  position: relative;
  aspect-ratio: 16 / 9;
  background: #10101a;
}

.media-card__thumb--square {
  aspect-ratio: 1 / 1;
}

.media-card__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.media-card__play {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.5rem;
  background: rgba(0, 0, 0, 0.25);
  transition: background 0.2s ease;
}

.media-card:hover .media-card__play {
  background: rgba(0, 0, 0, 0.4);
}

.media-modal {
  position: fixed;
  inset: 0;
  z-index: 1080;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
}

.media-modal__inner {
  position: relative;
  width: 100%;
  max-width: 960px;
}

.media-modal__body {
  background: #000;
  border-radius: 12px;
  overflow: hidden;
}

.media-modal__frame {
  display: block;
  width: 100%;
  max-height: 80vh;
  aspect-ratio: 16 / 9;
  object-fit: contain;
  border: 0;
}

.media-modal__caption {
  color: #fff;
  text-align: center;
  margin-top: 0.75rem;
}

.media-modal__close {
  position: absolute;
  top: -2.75rem;
  right: 0;
  width: 2.25rem;
  height: 2.25rem;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
}

.media-modal__close:hover {
  background: rgba(255, 255, 255, 0.3);
}

.skeleton {
  background: linear-gradient(90deg, #eee 25%, #f5f5f5 37%, #eee 63%);
  background-size: 400% 100%;
  animation: skeleton-loading 1.4s ease infinite;
}

.skeleton-line {
  height: 14px;
  border-radius: 4px;
}

@keyframes skeleton-loading {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .media-card,
  .skeleton {
    transition: none;
    animation: none;
  }
}
</style>
